export class ActivityBar {
  private track: HTMLElement;
  private roomName = '';
  private djName: string | null = null;
  private trackTitle: string | null = null;
  /** When true and no dj is set, the bar shows "DJ slot available" in
   *  place of the dj / now-playing segments. Used on the landing where
   *  the homepage advertises an open booth instead of an empty marquee. */
  private slotAvailable = false;

  constructor(root: HTMLElement) {
    this.track = root.querySelector<HTMLElement>('#activity-track') ?? root;
    this.render();
  }

  setRoom(name: string): void {
    this.roomName = name;
    this.render();
  }

  setNowPlaying(dj: string | null, track: string | null): void {
    this.djName = dj;
    this.trackTitle = track;
    this.render();
  }

  /** Toggle the "DJ slot available" fallback. The bar will only show it
   *  when there is no current dj — once one takes the booth the live
   *  metadata wins. */
  setSlotAvailable(available: boolean): void {
    this.slotAvailable = available;
    this.render();
  }

  private render(): void {
    const parts: string[] = [];

    if (this.roomName) {
      parts.push(`<span class="ab-item"><span class="ab-dot"></span><span class="ab-val">${esc(this.roomName)}</span></span>`);
    }

    if (this.djName) {
      parts.push(`<span class="ab-sep">✦</span>`);
      parts.push(`<span class="ab-item"><span class="ab-label">DJ</span><span class="ab-val">${esc(this.djName)}</span></span>`);
    } else if (this.slotAvailable) {
      parts.push(`<span class="ab-sep">✦</span>`);
      parts.push(`<span class="ab-item"><span class="ab-val ab-accent">DJ slot available</span></span>`);
    }

    if (this.trackTitle) {
      parts.push(`<span class="ab-sep">✦</span>`);
      parts.push(`<span class="ab-item ab-item--now"><span class="ab-label">Now Playing</span><span class="ab-val ab-accent">${esc(this.trackTitle)}</span></span>`);
    }

    const segment = parts.join('');
    const segmentHtml = `<div class="ab-segment">${segment}</div>`;
    // Render an initial pair so we can measure the natural segment width,
    // then top up with enough hidden copies that the track always overflows
    // the viewport by 2x — that's what keeps the scroll seamless instead of
    // visibly resetting from the middle each loop.
    this.track.innerHTML = segmentHtml + `<div class="ab-segment" aria-hidden="true">${segment}</div>`;

    requestAnimationFrame(() => {
      const first = this.track.querySelector<HTMLElement>('.ab-segment');
      if (!first) return;
      const segW = first.offsetWidth;
      if (segW === 0) return;
      const viewportW = this.track.parentElement?.offsetWidth ?? window.innerWidth;

      // Need at least 2 copies so the loop is seamless. Beyond that, add
      // copies until the track spans >= 2x viewport — that guarantees a
      // continuous stream of content at every animation phase.
      const minTrack = viewportW * 2;
      const copies = Math.max(2, Math.ceil(minTrack / segW));

      const copiesHtml =
        segmentHtml +
        `<div class="ab-segment" aria-hidden="true">${segment}</div>`.repeat(copies - 1);
      if (copies !== 2) this.track.innerHTML = copiesHtml;

      // Translate by exactly one segment per loop so the next copy lands in
      // the previous copy's slot — invisible seam.
      this.track.style.setProperty('--ab-shift', `${100 / copies}%`);

      const speed = 48;
      this.track.style.animationDuration = `${segW / speed}s`;
    });
  }
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;'
    : c === '<' ? '&lt;'
    : c === '>' ? '&gt;'
    : c === '"' ? '&quot;'
    : '&#39;',
  );
}
